import type { ReactNode } from 'react';
import { ErrorState, LoadingState, ModuleNotInPlanState } from '@assist/ui';
import { useSession } from '../state/session.js';
import type { Resource } from '../lib/useResource.js';

/**
 * Kerangka satu layar konsol: judul, sub-judul, aksi, dan KEEMPAT kondisi peta §5.
 *
 * MENGAPA IA MERENDER KONDISINYA SENDIRI ALIH-ALIH MENYERAHKANNYA KE LAYAR
 * -----------------------------------------------------------------------
 * Peta §5 menuntut kondisi yang sama dari setiap layar: baku, memuat (KERANGKA, bukan
 * pemintal — `70:354`), gagal (dengan kode galat yang dapat disebut ke dukungan —
 * `70:707`), dan modul di luar paket (`70:1007`, yang BUKAN halaman galat). Empat belas
 * layar yang masing-masing menuliskannya akan menghasilkan empat belas variasi, dan
 * variasi pada layar galat adalah variasi yang paling sedikit dilihat orang sebelum
 * rilis dan paling banyak dilihat pengguna sesudahnya.
 *
 * Jadi layar hanya menuliskan kondisi BAKU-nya. Ketiga yang lain dirender di sini, dari
 * satu `Resource`, dengan kalimat yang datang dari katalog dua bahasa.
 *
 * `moduleCode` membuat gerbang entitlement berlaku tanpa `if` di setiap layar: modul yang
 * tidak dibeli menghasilkan `70:1007` — dan bukan layar galat, yang akan menyalahkan
 * pengguna atas keputusan komersial organisasinya (UAT D-07).
 */
export function Screen<T>({
  title,
  titleAccent,
  subtitle,
  actions,
  resource,
  moduleCode,
  children,
  aside,
}: {
  title: string;
  /**
   * Bagian KEDUA judul, dirender `blue/55` — Figma menggambar judul dua warna di SETIAP
   * frame layar (`16:249`, `8:249`, `5:66`, …) dan sampai 28 September 2026 hanya tiga
   * layar memilikinya, karena ketiganya menuliskannya sendiri alih-alih lewat kerangka
   * ini. Ia DUA KUNCI dan bukan satu kalimat yang dipotong: urutan katanya MEMBALIK
   * antarbahasa ("Audits & Assessments" versus "Audit & Asesmen" masih searah, tetapi
   * "Operations Dashboard" versus "Dasbor Operasional" tidak), dan sebuah pemotong tidak
   * dapat mengetahui itu.
   */
  titleAccent?: string;
  subtitle?: string;
  actions?: ReactNode;
  /** `null` untuk layar yang tidak memuat apa pun dari server. */
  resource: Resource<T> | null;
  /** Modul yang harus ter-entitlement. Kosong berarti layar inti. */
  moduleCode?: string;
  /** Dirender hanya saat `resource.status === 'ready'`. */
  children: (data: T) => ReactNode;
  /** Dirender di bawah judul pada SETIAP kondisi — mis. bilah saringan. */
  aside?: ReactNode;
}) {
  const { t, hasModule, me } = useSession();

  const head = (
    <div className="page__head">
      <div>
        <h1 className="page__title">
          {title}
          {titleAccent ? (
            <>
              {' '}
              <span className="page__title-accent">{titleAccent}</span>
            </>
          ) : null}
        </h1>
        {subtitle ? <p className="page__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page__actions">{actions}</div> : null}
    </div>
  );

  if (moduleCode && !hasModule(moduleCode)) {
    return (
      <section className="page">
        {head}
        {/*
          Parameternya DIISI. `state.notInPlan.body` menuntut `{module}` dan
          `{organisation}`, dan memanggilnya tanpa keduanya membuat perender mengembalikan
          pesan APA ADANYA — yaitu `{module}` dan `{organisation}` MENTAH di layar (UAT
          U2-05). Gerbang teks antarmuka menangkapnya; sebelumnya bentuk cacat yang sama
          lolos karena gerbang itu hanya memeriksa keberadaan kuncinya.

          Varian `bodyNoOrg` dipakai ketika sesinya memang tidak terikat organisasi (aktor
          PQM): nama organisasi yang kosong akan menghasilkan kalimat yang terputus, dan
          itu lebih buruk daripada kalimat yang memang tidak menyebutkannya.
        */}
        <ModuleNotInPlanState
          title={t('state.notInPlan.title')}
          body={
            me?.tenant?.name
              ? t('state.notInPlan.body', {
                  module: moduleCode ?? '',
                  organisation: me.tenant.name,
                })
              : t('state.notInPlan.bodyNoOrg', { module: moduleCode ?? '' })
          }
          whoCanHelp={t('state.notInPlan.whoCanHelp')}
        />
      </section>
    );
  }

  return (
    <section className="page">
      {head}
      {aside}
      {resource === null ? (
        children(undefined as unknown as T)
      ) : resource.status === 'loading' ? (
        <LoadingState rows={6} label={t('state.loading.title')} />
      ) : resource.status === 'error' ? (
        <ErrorState
          title={t('state.error.title')}
          body={t('state.error.body')}
          code={resource.error?.problem.request_id ?? null}
          codeLabel={
            resource.error
              ? t('state.error.code', {
                  code: resource.error.problem.request_id ?? resource.error.problem.code,
                })
              : undefined
          }
          retryLabel={t('app.retry')}
          onRetry={() => void resource.reload()}
        />
      ) : (
        children(resource.data as T)
      )}
    </section>
  );
}
